import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  Application,
  ApplicationBoardFilter,
  ApplicationComment,
  ApplicationFile,
  ApplicationVote,
  ApplicationVotetype,
  BoardModal,
  DialogConfig,
  GenerateExcelFile,
  GetApplications,
  Job,
  JobBoard,
  JobModuleTranslationKeys,
  Pagination,
  RecruitmentProcess,
  SearchFilter,
  SortPipelineResult,
  TranslateKey,
  User,
} from '@job-v2/ats-utils';
import { environment } from '../../../environments/environment';
import { UiUtilsService } from '../../commons/services/UiUtils.service';
import {
  BehaviorSubject,
  catchError,
  firstValueFrom,
  lastValueFrom,
  Observable,
} from 'rxjs';
import { errorHandle } from '../../commons/errorHandling';
import { TranslateService } from '@ngx-translate/core';
import { WebSocketService } from '../../commons/services/webSocket.service';
import { WEB_SOCKET_EVENTS } from '../../commons/contants';

@Injectable({
  providedIn: 'root',
})
export class BoardService {
  private jobApiUrl = `${environment.apiUrl}/recruitment/jobs`;
  private applicationApiUrl = `${environment.apiUrl}/recruitment/applications`;
  private applicationFileApiUrl = `${environment.apiUrl}/recruitment/applications/files`;
  private applicationCommentApiUrl = `${environment.apiUrl}/recruitment/applications/comments`;
  private applicationHistoryApiUrl = `${environment.apiUrl}/history`;
  private uiUtils = inject(UiUtilsService);
  private translateService = inject(TranslateService);
  private readonly webSocketService = inject(WebSocketService)

  inviteRecruiterDialogConfig: DialogConfig = {
    visible: false,
    header: '',
    newEntity: false,
  };
  public readonly _BoardJobSource = new BehaviorSubject<JobBoard | undefined>(
    undefined,
  );
  public readonly boardJobSource$ = this._BoardJobSource.asObservable();

  private readonly _inviteRecruiterDialogSource =
    new BehaviorSubject<DialogConfig>(this.inviteRecruiterDialogConfig);
  public readonly inviteRecruiterDialogSource$ =
    this._inviteRecruiterDialogSource.asObservable();

  inviteCandidateDialogConfig: DialogConfig = {
    visible: false,
    header: '',
    newEntity: false,
  };
  private readonly _inviteCandidateDialogSource =
    new BehaviorSubject<DialogConfig>(this.inviteCandidateDialogConfig);
  public readonly inviteCandidateDialogSource$ =
    this._inviteCandidateDialogSource.asObservable();

  applicationDetailsDialogConfig: DialogConfig = {
    visible: false,
    header: '',
    newEntity: false,
  };
  private readonly _applicationDetailsDialogSource =
    new BehaviorSubject<DialogConfig>(this.applicationDetailsDialogConfig);
  public readonly applicationDetailsDialogSource$ =
    this._applicationDetailsDialogSource.asObservable();

  selectApplicationRejectionReasonDialogConfig: DialogConfig = {
    visible: false,
    header: '',
    newEntity: false,
  };
  private readonly _selectApplicationRejectionReasonDialogSource =
    new BehaviorSubject<DialogConfig>(
      this.selectApplicationRejectionReasonDialogConfig,
    );
  public readonly selectApplicationRejectionReasonDialogSource$ =
    this._selectApplicationRejectionReasonDialogSource.asObservable();


  visualizeRRDialogConfig: DialogConfig = {
    visible: false,
    header: '',
    newEntity: false,
  };
  private readonly _visualizeRRDialogSource =
    new BehaviorSubject<DialogConfig>(
      this.visualizeRRDialogConfig,
    );
  public readonly visualizeRRDialogSourceSource$ =
    this._visualizeRRDialogSource.asObservable();

  closeRecruitmentDialogConfig: DialogConfig = {
    visible: false,
    header: '',
    newEntity: false,
  };
  private readonly _closeRecruitmentDialogSource =
    new BehaviorSubject<DialogConfig>(
      this.closeRecruitmentDialogConfig,
    );
  public readonly closeRecruitmentDialogSource$ =
    this._closeRecruitmentDialogSource.asObservable();

  public readonly translateKey = TranslateKey;
  public readonly jobTranslateKey = JobModuleTranslationKeys;

  constructor(private httpClient: HttpClient) {}

  async getPipelinesApplications(
    paginationDetails: ApplicationBoardFilter,
  ): Promise<GetApplications> {
    const endpoint = `${this.applicationApiUrl}/get-by-pipelines`;
    return await lastValueFrom(
      this.httpClient.post<GetApplications>(endpoint, paginationDetails).pipe(
        catchError((error) => {
          return errorHandle(error);
        }),
      ),
    );
  }

  async getBoard(jobId: string): Promise<JobBoard> {
    const endpoint = `${this.jobApiUrl}/${jobId}/board`;
    return await lastValueFrom(
      this.httpClient.get<JobBoard>(endpoint).pipe(
        catchError((error) => {
          return errorHandle(error);
        }),
      ),
    );
  }

  async getRecruiterApplicationVote(
    applicationId: string,
    recruiterId: string,
  ): Promise<ApplicationVote[]> {
    const endpoint = `${this.applicationApiUrl}/votes/${applicationId}/recruiters/${recruiterId}`;
    return await lastValueFrom(
      this.httpClient.get<ApplicationVote[]>(endpoint).pipe(
        catchError((error) => {
          return errorHandle(error);
        }),
      ),
    );
  }

  async getApplicationNegativeVote(
    applicationId: string,
  ): Promise<ApplicationVote[]> {
    const endpoint = `${this.applicationApiUrl}/votes/${applicationId}/dislike`;
    return await lastValueFrom(
      this.httpClient.get<ApplicationVote[]>(endpoint).pipe(
        catchError((error) => {
          return errorHandle(error);
        }),
      ),
    );
  }

  async getApplicationPositiveVote(
    applicationId: string,
  ): Promise<ApplicationVote[]> {
    const endpoint = `${this.applicationApiUrl}/votes/${applicationId}/like`;
    return await lastValueFrom(
      this.httpClient.get<ApplicationVote[]>(endpoint).pipe(
        catchError((error) => {
          return errorHandle(error);
        }),
      ),
    );
  }

  async vote(applicationVote: ApplicationVote): Promise<ApplicationVote> {
    const endpoint = `${this.applicationApiUrl}/votes/handler`;
    return await lastValueFrom(
      this.httpClient.post<ApplicationVote>(endpoint, applicationVote).pipe(
        catchError((error) => {
          return errorHandle(error);
        }),
      ),
    );
  }

  async sortByPipelines(
    paginationDetails: Pagination,
  ): Promise<SortPipelineResult> {
    const endpoint = `${this.applicationApiUrl}/get-by-pipelines`;
    return await lastValueFrom(
      this.httpClient
        .post<SortPipelineResult>(endpoint, paginationDetails)
        .pipe(
          catchError((error) => {
            return errorHandle(error);
          }),
        ),
    );
  }

  async getJobRecruitmentReport (jobId: string): Promise<RecruitmentProcess> {
    const endpoint = `${this.jobApiUrl}/${jobId}/recruitment-report`;
    return await lastValueFrom(
      this.httpClient.get<RecruitmentProcess>(endpoint).pipe(
        catchError((error) => {
          return errorHandle(error);
        }),
      ),
    );
  }
  async updateApplicationsStatus(
    application: Application,
    columnId: string,
    notifyCandidate: boolean,
    recruitersMails: string[],
  ): Promise<SortPipelineResult> {
    const body = {
      oldPipelineId: application.status,
      newPipelineId: columnId,
      applicationId: application.id,
      candidateId: application.candidate,
      companyId: application.company,
      jobTitle: application.jobTitle,
      recruitersMails: recruitersMails,
      notifyCandidate: notifyCandidate,
    };
    const endpoint = `${this.applicationApiUrl}/update-pipeline`;
    return await lastValueFrom(
      this.httpClient.put<SortPipelineResult>(endpoint, body).pipe(
        catchError((error) => {
          return errorHandle(error);
        }),
      ),
    );
  }

  async updateMultipleApplicationsStatus(
    applications: Application[],
    columnId: string,
    notifyCandidate: boolean,
    recruitersMails: string[],
  ): Promise<SortPipelineResult> {
    const body = {
      applications: applications.map((item) => item.id),
      jobId: applications[0].job,
      companyId: applications[0].company,
      jobTitle: applications[0].jobTitle,
      recruitersMails,
      notifyCandidate,
      oldPipelineId: applications[0].status,
      newPipelineId: columnId,
    };
    const endpoint = `${this.applicationApiUrl}/massive-pipeline-update`;
    return await lastValueFrom(
      this.httpClient.put<SortPipelineResult>(endpoint, body).pipe(
        catchError((error) => {
          return errorHandle(error);
        }),
      ),
    );
  }

  async updateApplicationReadStatus(application: Application) {
    const endpoint = `${this.applicationApiUrl}/read-status`;
    return await firstValueFrom(
      this.httpClient.post(endpoint, {
        id: application.id,
      }),
    );
  }

  async extractCandidatesInformations(pipelineId: string, body: ApplicationBoardFilter): Promise<HttpResponse<Blob>> {
    const endpoint = `${this.applicationApiUrl}/extract/pipelines/${pipelineId}/candidates-informations`;
    return await lastValueFrom(
      this.httpClient.post(endpoint, body, {
        responseType: 'blob',
        observe: 'response',
      }).pipe(
        catchError((error) => {
          return errorHandle(error);
        }),
      ),
    );
  }

  async extractSelectedCandidatesInformations(applicationsIds: string[]): Promise<HttpResponse<Blob>> {
    const endpoint = `${this.applicationApiUrl}/extract/candidates-informations`;
    return await lastValueFrom(
      this.httpClient.post(endpoint, applicationsIds, {
        responseType: 'blob',
        observe: 'response',
      }).pipe(
        catchError((error) => {
          return errorHandle(error);
        }),
      ),
    );
  }

  async downloadPipelines(pipeline: string): Promise<HttpResponse<Blob>> {
    const endpoint = `${this.applicationApiUrl}/download/pipelines/${pipeline}/cvs`;

    return await lastValueFrom(
      this.httpClient
        .get(endpoint, {
          responseType: 'blob',
          observe: 'response',
        })
        .pipe(
          catchError((error) => {
            return errorHandle(error);
          }),
        ),
    );
  }

  async downloadCvsSelected(
    applications: Application[],
  ): Promise<HttpResponse<Blob>> {
    const endpoint = `${this.applicationApiUrl}/download/documents`;

    return await lastValueFrom(
      this.httpClient
        .post(endpoint, applications, {
          responseType: 'blob',
          observe: 'response',
        })
        .pipe(
          catchError((error) => {
            return errorHandle(error);
          }),
        ),
    );
  }

  async closeRecruitmentProcess(jobId: string) {
    const endpoint = `${this.jobApiUrl}/close-recruitment-process`;

    return await lastValueFrom(
      this.httpClient
        .patch(endpoint, {
              jobId,
              shouldAutoRejectPendingCandidates: true
        })
        .pipe(
          catchError((error) => {
            return errorHandle(error);
          }),
        ),
    );
  }

  async initApplicationVotingProcess(
    application: Application,
    voteType: ApplicationVotetype,
    user: User,
  ): Promise<Application> {
    const applicationVotes = application.recruiterVotes || [];
    const userVote = applicationVotes.find((vote) => vote.user === user._id);

    const body: ApplicationVote = {
      id: userVote?.id || '',
      application: application.id as string,
      user: user._id as string,
      voteType: voteType,
    };
    let response: ApplicationVote | null = null
    try {
      response = await this.vote(body);
    } catch (error) {
      response = null
    }

    if (response) {
      application.recruiterVotes = application.recruiterVotes.filter(
        (vote) => vote.user !== user._id,
      );
      application.applicationPositiveVotes =
        application.applicationPositiveVotes?.filter(
          (vote) => vote.user !== user._id,
        );
      application.applicationNegativeVotes =
        application.applicationNegativeVotes?.filter(
          (vote) => vote.user !== user._id,
        );

      if (response.voteType !== null) {
        application.recruiterVotes = [...application.recruiterVotes, response];

        if (response.voteType === ApplicationVotetype.LIKE) {
          application.applicationPositiveVotes = [
            ...(application.applicationPositiveVotes || []),
            response,
          ];
        } else {
          application.applicationNegativeVotes = [
            ...(application.applicationNegativeVotes || []),
            response,
          ];
        }
      }
    }

    return application;
  }

  showDialog(
    header: string,
    newEntity: boolean,
    data: any,
    dialog: BoardModal,
  ): void {
    if (dialog === BoardModal.INVITE_RECRUITER) {
      this.inviteRecruiterDialogConfig = {
        visible: true,
        header,
        newEntity,
        isUpdate: !newEntity,
        data,
      };
      this._inviteRecruiterDialogSource.next(this.inviteRecruiterDialogConfig);
    } else if (dialog === BoardModal.CLOSE_RECRUITMENT) {
      this.closeRecruitmentDialogConfig = {
        visible: true,
        header,
        newEntity,
        isUpdate: !newEntity,
        data,
      };
      this._closeRecruitmentDialogSource.next(
        this.closeRecruitmentDialogConfig,
      );
    } else if (dialog === BoardModal.SELECT_APPLICATION_REJECTION_REASON) {
      this.selectApplicationRejectionReasonDialogConfig = {
        visible: true,
        header,
        newEntity,
        isUpdate: !newEntity,
        data,
      };
      this._selectApplicationRejectionReasonDialogSource.next(
        this.selectApplicationRejectionReasonDialogConfig,
      );
    } else if (dialog === BoardModal.VISUALIZE_RECRUITMENT_REPORT) {
      this.visualizeRRDialogConfig = {
        visible: true,
        header,
        newEntity,
        isUpdate: !newEntity,
        data,
      };
      this._visualizeRRDialogSource.next(
        this.visualizeRRDialogConfig,
      );
    } else if (dialog === BoardModal.INVITE_CANDIDATE) {
      this.inviteCandidateDialogConfig = {
        visible: true,
        header,
        newEntity,
        isUpdate: !newEntity,
        data,
      };
      this._inviteCandidateDialogSource.next(this.inviteCandidateDialogConfig);
    } else {
      this.applicationDetailsDialogConfig = {
        visible: true,
        header,
        newEntity,
        isUpdate: !newEntity,
        data,
      };
      this._applicationDetailsDialogSource.next(
        this.applicationDetailsDialogConfig,
      );
    }
  }

  closeDialog(dialog: BoardModal): void {
    if (dialog === BoardModal.INVITE_RECRUITER) {
      this.inviteRecruiterDialogConfig = {
        visible: false,
        data: null,
        header: '',
        newEntity: false,
        isUpdate: false,
      };
      this._inviteRecruiterDialogSource.next(this.inviteRecruiterDialogConfig);
    } else if (dialog === BoardModal.CLOSE_RECRUITMENT) {
      this.closeRecruitmentDialogConfig = {
        visible: false,
        header: '',
        newEntity: false,
        isUpdate: false,
        data: null,
      };
      this._closeRecruitmentDialogSource.next(
        this.closeRecruitmentDialogConfig,
      );
    } else if (dialog === BoardModal.SELECT_APPLICATION_REJECTION_REASON) {
      this.selectApplicationRejectionReasonDialogConfig = {
        visible: false,
        header: '',
        newEntity: false,
        isUpdate: false,
        data: null,
      };
      this._selectApplicationRejectionReasonDialogSource.next(
        this.selectApplicationRejectionReasonDialogConfig,
      );
    } else if (dialog === BoardModal.INVITE_CANDIDATE) {
      this.inviteCandidateDialogConfig = {
        visible: false,
        header: '',
        newEntity: false,
        isUpdate: false,
        data: null,
      };
      this._inviteCandidateDialogSource.next(this.inviteCandidateDialogConfig);
    }else if (dialog === BoardModal.VISUALIZE_RECRUITMENT_REPORT) {
      this.visualizeRRDialogConfig = {
        visible: false,
        header: '',
        newEntity: false,
        isUpdate: false,
        data: null,
      };
      this._visualizeRRDialogSource.next(this.visualizeRRDialogConfig);
    } else {
      this.applicationDetailsDialogConfig = {
        visible: false,
        data: null,
        header: '',
        newEntity: false,
        isUpdate: false,
      };
      this._applicationDetailsDialogSource.next(
        this.applicationDetailsDialogConfig,
      );
    }
  }

  async attachNewFileToApplication(
    applicationId: string,
    formData: FormData,
  ): Promise<ApplicationFile> {
    const endpoint = `${this.applicationFileApiUrl}/${applicationId}`;
    return await lastValueFrom(
      this.httpClient.post<ApplicationFile>(endpoint, formData).pipe(
        catchError((error) => {
          return errorHandle(error);
        }),
      ),
    );
  }

  async getApplicationsHistoric(
    applicationId: string,
  ): Promise<{ data: string; createdAt: string }[]> {
    const endpoint = `${this.applicationHistoryApiUrl}/applications/${applicationId}`;
    return await lastValueFrom(
      this.httpClient.get<{ data: string; createdAt: string }[]>(endpoint).pipe(
        catchError((error) => {
          return errorHandle(error);
        }),
      ),
    );
  }

  async getApplicationsFiles(
    applicationId: string,
  ): Promise<ApplicationFile[]> {
    const endpoint = `${this.applicationFileApiUrl}/${applicationId}/all`;
    return await lastValueFrom(
      this.httpClient.get<ApplicationFile[]>(endpoint).pipe(
        catchError((error) => {
          return errorHandle(error);
        }),
      ),
    );
  }

  async deleteApplicationFile(fileId: string): Promise<ApplicationFile> {
    const endpoint = `${this.applicationFileApiUrl}/${fileId}`;
    return await lastValueFrom(
      this.httpClient.delete<ApplicationFile>(endpoint).pipe(
        catchError((error) => {
          return errorHandle(error);
        }),
      ),
    );
  }

  async updateApplication(
    applicationId: string,
    application: Partial<Application>,
  ): Promise<Application> {
    const endpoint = `${this.applicationApiUrl}/${applicationId}/update`;
    return await lastValueFrom(
      this.httpClient.put<Application>(endpoint, application).pipe(
        catchError((error) => {
          return errorHandle(error);
        }),
      ),
    );
  }
  async saveComment(body: ApplicationComment): Promise<ApplicationComment> {
    const endpoint = `${this.applicationCommentApiUrl}`;
    return await lastValueFrom(
      this.httpClient.post<ApplicationComment>(endpoint, body).pipe(
        catchError((error) => {
          return errorHandle(error);
        }),
      ),
    );
  }

  async getComments(applicationId: string): Promise<ApplicationComment[]> {
    const endpoint = `${this.applicationCommentApiUrl}/${applicationId}/all`;
    return await lastValueFrom(
      this.httpClient.get<ApplicationComment[]>(endpoint).pipe(
        catchError((error) => {
          return errorHandle(error);
        }),
      ),
    );
  }

  async deleteComment(commentId: string): Promise<ApplicationComment> {
    const endpoint = `${this.applicationCommentApiUrl}/${commentId}`;
    return await lastValueFrom(
      this.httpClient.delete<ApplicationComment>(endpoint).pipe(
        catchError((error) => {
          return errorHandle(error);
        }),
      ),
    );
  }

  async handleReadStatus(application: Application, user: User) {
    const readedBy = application.readBy || [];
    if (readedBy.includes(user._id as string)) return;
    await this.updateApplicationReadStatus(application).then(() => {
      application.readBy = [...readedBy, user._id as string];
    });
  }

  search(filters: SearchFilter[], jobId: string): Observable<Application[]> {
    return this.httpClient
      .post<Application[]>(`${this.jobApiUrl}/${jobId}/search`, filters)
      .pipe(
        catchError((error) => {
          return errorHandle(error);
        }),
      );
  }

  initWebSocket (applicationsInProcess: number): void {
    this.webSocketService.initializeConnection();

    this.webSocketService
    .listen(WEB_SOCKET_EVENTS.APPLICATION_STATUS_UPDATE_STARTED)
    .subscribe({
      next: () => {
        this.webSocketService.setWebSocketJobCounterLimit(applicationsInProcess)
      },
    });

    this.webSocketService
    .listen(WEB_SOCKET_EVENTS.APPLICATION_STATUS_UPDATE_FAILED)
    .subscribe({
      next: () => {
        this.webSocketService.updateWebSocketJobFailedCounter()
      },
    });

    this.webSocketService
    .listen(WEB_SOCKET_EVENTS.APPLICATION_STATUS_UPDATE_COMPLETED)
    .subscribe({
      next: () => {
        this.webSocketService.updateWebSocketJobCounter()
      },
    });
  }
}
